import { normalizeCatalogSort, type CatalogFilters } from './catalog';
import { normalizeFacetSelections } from './facets';
import type { CategoryId, Market } from '../types';
import { readCatalogPage } from './pagination';

export const emptySearchFilters = (): CatalogFilters => ({ query: '', category: 'all', sellerCountry: 'all', minPrice: '', maxPrice: '', condition: 'all', brand: '', sort: 'newest', facets: {}, page: 1 });
const categories = ['cameras', 'lenses', 'telescopes', 'mounts', 'accessories'];
const markets = ['IT', 'CH', 'FR', 'ES'];
const boundedText = (value: string | null, maximum: number) => [...(value || '')].filter(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127).join('').trim().slice(0, maximum);
const price = (value: string | null) => value && /^\d{1,5}(\.\d{1,2})?$/.test(value) && Number(value) <= 50000 ? value : '';

/** A bookmark carries only public search filters, never account or tracking data. */
export function readSearchFilters(hash: string): CatalogFilters {
  const defaults = emptySearchFilters();
  if (hash.split('?')[0] !== '#/search') return defaults;
  const params = new URLSearchParams(hash.split('?').slice(1).join('?'));
  const category = params.get('category');
  const sellerCountry = params.get('country');
  const condition = params.get('condition');
  const sort = params.get('sort');
  const facets: Record<string, string[]> = {};
  params.forEach((value, key) => { if (key.startsWith('facet_') && key.length <= 60) facets[key.slice(6)] = [value]; });
  const chosenCategory = categories.includes(category || '') ? category as CategoryId : 'all';
  return { ...defaults, query: boundedText(params.get('q'), 120), category: chosenCategory,
    sellerCountry: markets.includes(sellerCountry || '') ? sellerCountry as Market : 'all',
    condition: ['new', 'excellent', 'good', 'fair'].includes(condition || '') ? condition as CatalogFilters['condition'] : 'all',
    minPrice: price(params.get('min')), maxPrice: price(params.get('max')), brand: boundedText(params.get('brand'), 80),
    sort: normalizeCatalogSort(sort), facets: normalizeFacetSelections(chosenCategory, facets), page: readCatalogPage(params.get('page')) };
}

export function searchHash(filters: CatalogFilters): string {
  const params = new URLSearchParams();
  const query = boundedText(filters.query, 120);
  if (query) params.set('q', query);
  if (categories.includes(filters.category)) params.set('category', filters.category);
  if (markets.includes(filters.sellerCountry)) params.set('country', filters.sellerCountry);
  if (filters.minPrice) params.set('min', filters.minPrice.slice(0, 12));
  if (filters.maxPrice) params.set('max', filters.maxPrice.slice(0, 12));
  if (filters.condition !== 'all') params.set('condition', filters.condition);
  const brand = boundedText(filters.brand, 80);
  if (brand) params.set('brand', brand);
  const sort = normalizeCatalogSort(filters.sort); if (sort !== 'newest') params.set('sort', sort);
  const facets = normalizeFacetSelections(filters.category, filters.facets || {});
  for (const key of Object.keys(facets).sort()) if (facets[key].length) params.set(`facet_${key}`, facets[key][0]);
  const page = readCatalogPage(filters.page ?? 1); if (page > 1) params.set('page', String(page));
  return `#/search${params.size ? `?${params}` : ''}`;
}
